import type { SiteInfo } from '../../views/CalendarMultiView'
import { GanttTask, GanttMilestone } from '../../types/gantt.types'
import { RH, DN, MILESTONE_COLORS, MILESTONE_DEFAULT_COLOR, PRINT_TOKEN_VARS } from '../../constants/gantt.const'
import { formatISO, escapeHtml } from '../../constants/gantt.logic'
import { groupTasksIntoRows } from '../../views/scheduleUtils'
import { toast } from 'sonner'

// 인쇄(PDF) 창 생성 — 새 창에 HTML 문자열을 써서 인쇄한다. 이동 전 GanttView.tsx 의 handlePDF 본문을 그대로 옮긴 것이다.
export type GanttPrintArgs = {
  selectedSite: SiteInfo | undefined
  tasks: GanttTask[]
  milestones: GanttMilestone[]
  ganttBounds: { start: Date; end: Date }
  showCont: boolean
  user: { compName?: string | null } | null | undefined
  scheduleRemark: string
  holidays: Set<string>
  /** 공종의 소요일(작업일 수, A-09) — 공휴일 정보를 쓸 수 없으면 null(막대 안 소요일 글자를 그리지 않는다) */
  durationOf: (t: GanttTask) => number | null
}

export function printGantt(args: GanttPrintArgs) {
  const { selectedSite, tasks, milestones, ganttBounds, showCont, user, scheduleRemark, holidays, durationOf } = args
      // 조용히 끝내지 않고 이유를 알린다(F-16)
      if (!selectedSite) { toast.info('현장을 먼저 선택해 주세요'); return }
      if (tasks.length === 0) { toast.info('출력할 공종이 없어요'); return }
      const win = window.open('', '_blank', 'width=1200,height=900')
      if (!win) { toast.error('팝업이 차단되어 열 수 없어요. 팝업을 허용한 뒤 다시 눌러 주세요'); return }

      const h     = selectedSite
      const gs    = ganttBounds.start
      const ge    = ganttBounds.end
      const tDays = Math.max(Math.round((ge.getTime() - gs.getTime()) / 86400000) + 1, 1)
      const rH    = showCont ? 52 : RH

      // 공종 열 너비: 최장 공종명 기준 동적 계산 (한 글자 ≈ 10px + 패딩·도트)
      const maxNameLen = Math.max(...tasks.map(t => t.workTypeName.length), 2)
      const leftW = Math.min(150, Math.max(64, maxNameLen * 10 + 34))

      // A4 landscape: (297-16)mm ≈ 1062px @96dpi — 전체를 한 장 가로에 담도록 DW 동적 계산
      const A4W = 1062
      const DW  = Math.max(4, Math.floor((A4W - leftW) / tDays))
      const pgW = leftW + tDays * DW

      // 날짜 배열 (오늘 강조 없음)
      const allDays = Array.from({ length: tDays }, (_, i) => {
        const d = new Date(gs); d.setDate(gs.getDate() + i); d.setHours(0,0,0,0)
        return { lbl: `${d.getMonth()+1}/${d.getDate()}`, dow: DN[d.getDay()],
                 su: d.getDay()===0, sa: d.getDay()===6, holiday: holidays.has(formatISO(d)), date: d }
      })

      // 마일스톤 날짜 맵
      const msMap = new Map<number, GanttMilestone[]>()
      milestones.forEach(m => {
        const md = new Date(m.milestoneDate); md.setHours(0,0,0,0)
        const day = Math.round((md.getTime() - gs.getTime()) / 86400000)
        if (day >= 0 && day < tDays) { const arr = msMap.get(day) ?? []; arr.push(m); msMap.set(day, arr) }
      })

      const fmtD = (d: Date) =>
        `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`

      // 월 헤더
      const monthGroups: { label: string; span: number }[] = []
      let curMG = { label: '', span: 0 }
      allDays.forEach(d => {
        const key = `${d.date.getFullYear()}년 ${d.date.getMonth()+1}월`
        if (curMG.label !== key) { if (curMG.span) monthGroups.push(curMG); curMG = { label: key, span: 0 } }
        curMG.span++
      })
      if (curMG.span) monthGroups.push(curMG)
      const monthHeaderHtml = monthGroups.map(g =>
        `<div style="width:${g.span*DW}px;min-width:${g.span*DW}px;padding:2px 5px;font-size:8px;font-weight:700;color:var(--shell-on-surface);border-right:1px solid var(--shell-border);white-space:nowrap;overflow:hidden">${g.label}</div>`
      ).join('')

      // 날짜 헤더 셀 (오늘 강조 제거)
      const headerCells = allDays.map((d, i) => {
        const ms  = msMap.get(i)
        const bg  = (d.su || d.sa || d.holiday) ? 'var(--alpha-black-50)' : 'transparent'
        const col = (d.su || d.holiday) ? 'var(--status-danger-600)' : d.sa ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)'
        const msH = ms && DW >= 10 ? ms.map(m => { const c = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR; return `<div style="font-size:5.5px;color:${c};font-weight:700">◆</div>` }).join('') : ''
        const text = DW >= 18
          ? `<div style="font-size:7px">${d.lbl}</div><div style="font-size:5.5px">${d.dow}</div>${msH}`
          : DW >= 10 ? `<div style="font-size:6px">${d.date.getDate()}</div>${msH}`
          : ''
        return `<div style="width:${DW}px;min-width:${DW}px;text-align:center;padding:1px 0;border-right:1px solid var(--shell-border);background:${bg};color:${col}">${text}</div>`
      }).join('')

      // 공종 행 (바: 소요일만 표시, 오늘 배경 없음) — 동일 공종(workTypeCode)이 여러 번
      // 등록됐으면 라이브 렌더와 동일하게 groupTasksIntoRows로 묶어서 한 행에 바를 여러 개 그린다.
      const pdfRows = groupTasksIntoRows(tasks)
      const taskRows = pdfRows.map(row => {
        const primary = row.bars[0]
        const barsHtml = row.bars.map(task => {
          const s        = Math.round((task.startDate.getTime() - gs.getTime()) / 86400000)
          const l        = Math.max(1, Math.round((task.endDate.getTime() - task.startDate.getTime()) / 86400000) + 1)
          const barLeft  = s * DW + 1
          const barWidth = Math.max(DW - 2, l * DW - 2)
          // 막대 길이(칸 수)는 달력 기준 그대로 두고 글자만 작업일 수(A-09)
          const wd       = durationOf(task)
          const barLabel = DW >= 12 && wd != null ? `${wd}일` : ''
          return `<div style="position:absolute;height:18px;border-radius:3px;top:${Math.floor((rH-18)/2)}px;left:${barLeft}px;width:${barWidth}px;background:${escapeHtml(task.color)};display:flex;align-items:center;padding:0 4px;font-size:6.5px;color:var(--shell-surface);font-weight:700;white-space:nowrap;overflow:hidden">${barLabel}</div>`
        }).join('')

        const dayCells = allDays.map(() =>
          `<div style="width:${DW}px;min-width:${DW}px;height:${rH}px;border-right:1px solid var(--shell-border)"></div>`
        ).join('')
        const contH = showCont && (primary.foremanName || primary.foremanContact)
          ? `<div style="font-size:7px;color:var(--shell-on-surface-2);padding-left:12px">${primary.foremanName?`<div>${escapeHtml(primary.foremanName)}</div>`:''}${primary.foremanContact?`<div style="color:var(--shell-hint)">${escapeHtml(primary.foremanContact)}</div>`:''}</div>` : ''

        return `<div style="display:flex;border-bottom:1px solid var(--shell-border);position:relative;min-height:${rH}px;background:var(--shell-surface)">
  <div style="width:${leftW}px;min-width:${leftW}px;padding:3px 8px;border-right:1px solid var(--shell-border);min-height:${rH}px;display:flex;flex-direction:column;justify-content:center;gap:2px"><div style="display:flex;align-items:center;gap:5px"><div style="width:6px;height:6px;border-radius:50%;background:${escapeHtml(primary.color)};flex-shrink:0"></div><span style="font-size:9px;color:var(--shell-on-surface);font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(row.workTypeName)}</span></div>${contH}</div>
  <div style="display:flex;position:relative;width:${tDays*DW}px;min-width:${tDays*DW}px;height:${rH}px;overflow:visible"><div style="display:flex">${dayCells}</div>${barsHtml}</div>
  </div>`
      }).join('')

      // 인디피아 로고 — 헤더 우측 상단. LeftNav 와 같은 public 에셋(/brand/indipia-symbol.svg)을 참조한다.
      // 새 창은 about:blank 라 상대경로가 불안정하므로 origin 을 붙인다.
      const logoSvg = `<img src="${window.location.origin}/brand/indipia-symbol.svg" width="14" height="14" alt="">`
      // 새 창엔 tokens.css 가 없다 — 인쇄 HTML 이 쓰는 토큰만 현재 문서의 계산값으로 복사한다.
      const rootStyle = getComputedStyle(document.documentElement)
      const printTokenVars = PRINT_TOKEN_VARS.map(v => `${v}:${rootStyle.getPropertyValue(v).trim()};`).join('')

      const compLabel = [user?.compName, h.siteName || '—'].filter(Boolean).join(' - ')
      // 스케줄 관리 화면의 비고 카드 UI와 동일한 스타일 — 간트차트 아래에 배치
      const remarkHtml = scheduleRemark
        ? `<div class="pdf-remark" style="background:var(--shell-bg);border-top:1px solid var(--shell-border);padding:5px 8px 6px">
  <div style="background:var(--shell-surface);border:1.2px solid var(--shell-border);border-radius:8px;overflow:hidden">
  <div style="display:flex;align-items:center;gap:5px;padding:4px 8px;border-bottom:1px solid var(--shell-border);background:var(--shell-bg)">
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  <span style="font-size:8px;font-weight:800;color:var(--shell-on-surface)">비고</span>
  <span style="font-size:6.5px;color:var(--shell-hint)">현장 전체 일정에 대한 전달사항입니다.</span>
  </div>
  <div style="padding:5px 8px;font-size:7.5px;color:var(--shell-on-surface);line-height:1.4;white-space:pre-wrap;overflow-wrap:break-word">${escapeHtml(scheduleRemark)}</div>
  </div>
  </div>`
        : ''

      win.document.write(`<!DOCTYPE html><html><head>
  <meta charset="utf-8"><title>${escapeHtml(h.siteName||'')} 간트차트</title>
  <style>
  :root{${printTokenVars}}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,'Apple SD Gothic Neo','Malgun Gothic',sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:A4 landscape;margin:5mm}
  .pdf-header{page-break-inside:avoid;break-inside:avoid}
  .pdf-remark{page-break-inside:avoid;break-inside:avoid}
  </style>
  </head><body>
  <div style="width:${pgW}px;margin:0 auto">
  <div class="pdf-header" style="padding:3px 12px;border-bottom:2px solid var(--shell-border)">
  <div style="display:flex;align-items:center;justify-content:space-between;gap:10px">
  <span style="font-size:11px;font-weight:800;color:var(--shell-on-surface)">${escapeHtml(compLabel)} (${fmtD(gs)} ~ ${fmtD(ge)})</span>
  <div style="display:flex;align-items:center;gap:4px;flex-shrink:0">${logoSvg}<span style="font-size:8.5px;font-weight:800;color:var(--shell-on-surface)">인디피아</span></div>
  </div>
  </div>
  <div style="display:flex;background:var(--shell-bg);border-bottom:1px solid var(--shell-border);align-items:center;height:20px">
  <div style="width:${leftW}px;min-width:${leftW}px;border-right:1px solid var(--shell-border);height:20px"></div>
  <div style="display:flex">${monthHeaderHtml}</div>
  </div>
  <div style="display:flex;background:var(--shell-bg);border-bottom:1px solid var(--shell-border);align-items:stretch">
  <div style="width:${leftW}px;min-width:${leftW}px;padding:3px 8px;font-size:8.5px;font-weight:700;color:var(--shell-on-surface-2);border-right:1px solid var(--shell-border);display:flex;align-items:center">공종</div>
  <div style="display:flex">${headerCells}</div>
  </div>
  ${taskRows}
  ${remarkHtml}
  </div>
  </body></html>`)
      win.document.close()
      // 인라인 script 대신 부모 창에서 직접 호출 — CSP 우회
      win.onafterprint = () => win.close()
      const printNow = () => { win.focus(); win.print() }
      const logoImg = win.document.images[0]
      setTimeout(() => {
        if (logoImg && !logoImg.complete) { logoImg.onload = printNow; logoImg.onerror = printNow }
        else printNow()
      }, 300)
}
